import React, { useEffect, useRef } from 'react';
import anime from 'animejs';
import Orb from './components/Backgrounds/Orb/Orb';
import './App.css';

function App() {
  const sectionRefs = useRef([]);

  useEffect(() => {
    // Hero section animations
    anime({
      targets: '.figment-logo',
      translateY: [-50, 0],
      opacity: [0, 1],
      easing: 'easeOutCubic',
      duration: 1000, // Slightly increased duration for smoothness
      delay: 200,
    });

    anime({
      targets: '.tagline',
      translateY: [-50, 0],
      opacity: [0, 1],
      easing: 'easeOutCubic',
      duration: 1000, // Slightly increased duration for smoothness
      delay: 400,
    });

    anime({
      targets: '.github-button',
      translateY: [-50, 0],
      opacity: [0, 1],
      easing: 'easeOutCubic',
      duration: 1000, // Slightly increased duration for smoothness
      delay: 600,
    });

    // Scroll animations for other sections
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            anime({
              targets: entry.target.querySelectorAll('h2, p, .workflow-steps, .solution-details, .key-benefit, .linkedin-icon'),
              translateY: [50, 0],
              opacity: [0, 1],
              easing: 'easeOutCubic',
              duration: 1200, // Increased duration for smoothness
              delay: anime.stagger(80), // Slightly reduced stagger for cohesion
            });
            observer.unobserve(entry.target); // Only animate once
          }
        });
      },
      {
        threshold: 0.2, // Trigger when 20% of the section is visible
      }
    );

    sectionRefs.current.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      sectionRefs.current.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  return (
    <div className="App">
      <Orb hue={240} /> {/* Adjusted hue for blue/purple gradient */}

      <div className="content">
        <section className="hero-section">
          <img src="Figment.png" alt="Figment Logo" className="figment-logo" />
          <p className="tagline">The Missing Piece to AI Coding Assistance</p>
          <a href="https://github.com/JoshuaPaul20/Figment" className="github-button">Get Figment on GitHub</a>
        </section>

        <section className="problem-statement" ref={(el) => (sectionRefs.current[0] = el)}>
          <h2>The Problem</h2>
          <p>AI platforms help code faster but lack a focus on cohesive UI/UX with random colors being generated and inconsistent frontends across enterprises.</p>
        </section>

        {/* How it Works Section */}
        <section className="how-it-works-section" ref={(el) => (sectionRefs.current[1] = el)}>
          <h2>How Figment Works</h2>
          <div className="workflow-steps">
            <div className="step">
              <h3>Step 1: Define Your Vision</h3>
              <p>Input your brand guidelines and design system tokens into Figment's CLI. This becomes your Model Context Protocol (MCP) for consistent UI/UX.</p>
            </div>
            <div className="step">
              <h3>Step 2: AI-Powered Cohesion</h3>
              <p>As AI coding assistants like Gemini CLI and Claude Code generate components, Figment's MCP ensures they align perfectly with your defined standards.</p>
            </div>
            <div className="step">
              <h3>Step 3: Live Refinement & Export</h3>
              <p>Designers and Vibe Coders use the web version for live previews, font/color scheme selection, and Figma token integration. Export flawless, consistent code.</p>
            </div>
          </div>
        </section>

        <section className="solution-section" ref={(el) => (sectionRefs.current[2] = el)}>
          <h2>Figment: Your Model Context Protocol</h2>
          <div className="solution-details">
            <div className="cli-version">
              <h3>CLI Version (for Devs)</h3>
              <p>Easily input your brand guide and act as an MCP for all components coming out of Claude Code and Gemini CLI.</p>
            </div>
            <div className="web-version">
              <h3>Web Version (for Designers & Vibe Coders)</h3>
              <p>Select and get a live preview of fonts, color schemes, and even input your Figma design tokens.</p>
            </div>
          </div>
          <p className="key-benefit">Figment: Flawless UI/UX. From the First Line.</p>
        </section>

        <footer className="main-footer" ref={(el) => (sectionRefs.current[3] = el)}>
          <a href="https://www.linkedin.com/in/joshua-paul-dev/" target="_blank" rel="noopener noreferrer" className="linkedin-icon">
            <img src="icons8-linkedin-50.png" alt="LinkedIn" />
          </a>
        </footer>
      </div>
    </div>
  );
}

export default App;